import React, { useState, useEffect, useRef } from 'react';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { SplitButton } from 'primereact/splitbutton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import OlWKT from "ol/format/WKT";
import OlFeature from 'ol/Feature';

import { i18n } from '../../components';

import ResultsTablesTabs from './components/ResultsTablesTabs';


export default function IntersectResults(props) {

    const {viewer, core, Models, actions, dispatch, mainMap, componentId, intersectConfig, intersectLayer, searchParams, results, groupResults } = props;

    const { viewer_add_dialog_window, viewer_update_dialog_window } = actions;

    const { showOnPortal } = Models.Utils;

    const [showGeometry, setShowGeometry] = useState(false);
    const [items, setItems] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);
    const [selectAll, setSelectAll] = useState({});
    const [selectedRows, setSelectedRows] = useState({});
    const [downloadingFile, setDownloadingFile] = useState(false);
    const [downloadingFormat, setDownloadingFormat] = useState(false);

    const toastDialog = useRef(null);

    const t = i18n.translateValue;

    const fileFormats = {
        "xls": {"icon": "pi-file-excel", label: "Excel"},
        "csv": {"icon": "pi-file", label: "CSV"},
        "pdf": {"icon": "pi-file-pdf", label: "PDF"},
        "gpkg": {"icon": "pi-file", label: "GeoPackage"}
    }
    const exportFormats = intersectConfig?.exportFormats?.length ? intersectConfig.exportFormats.map(f => f.toLowerCase()) : ['xls', 'csv', 'pdf', 'gpkg'];

    const showResultMeasure = intersectConfig?.showResultMeasure !=null ? intersectConfig?.showResultMeasure : true;
    const showResultPercentage = intersectConfig?.showResultPercentage != null ? intersectConfig?.showResultPercentage : true;

    const showTablesButton = !!intersectConfig?.showTables;


    const groupResultsData = (results) => {
        const groups = {};
        results?.layers?.forEach((l, index) => {
            const group = l.group;
            if (!groups[l.group]) {
                groups[l.group] = {
                    key: String(index),
                    group: group,
                    title: group,
                    results: []
                }
            }
            const results = l.results || [];
            results.forEach((r, index) => {
                groups[group].results.push(
                    {
                        key: String(index),
                        fields: l.fields,
                        ...r
                    }
                )
            });
        });
        return Object.keys(groups).map((key) => groups[key]);
    }

    const selectFeature = (rowData, layerKey) => {

        if (!intersectLayer?.current) return;

        let feature;
        intersectLayer.current.getSource().getFeatures().forEach((f) => {
            f.set('_selected', false);
            if (f.get('_componentId') == componentId && f.get('_type') == 'results' && f.get('_layerKey') == layerKey) {
                if (f.get('key') == rowData.key) {
                    feature = f;
                }
            }
        });

        if (feature) {
            if (intersectConfig?.highlightFeatures !== false) {
                feature.set('_selected', true);
            }
            if (intersectConfig?.zoomToFeatures !== false) {
                const geom = feature.getGeometry();
                if (geom) {
                    let extent = geom.getExtent();
                    if (extent) {
                        extent = [extent[0] - 250, extent[1] - 250, extent[2] + 250, extent[3] + 250];
                        const center = [extent[0] + (extent[2] - extent[0])/2, extent[1] + (extent[3] - extent[1])/2];
                        dispatch(actions.map_set_extent(center, extent));
                    }
                }
            }
        }

        mainMap.getView().changed();
    }

    const exportResults = (format) => {
        let url = '/spatial_toolbox/api/intersect';
        if (intersectConfig?.apiUrl) {
            url = intersectConfig.apiUrl;
        }
        if (intersectConfig?.export?.[format]?.apiUrl) {
            url = intersectConfig.export[format].apiUrl;
        }

        const export_srid = intersectConfig?.export_crs || viewer?.config_json?.export_crs || viewer?.config_json?.display_crs;

        const query = new URLSearchParams({...searchParams, format, export_srid: export_srid, viewerId: viewer.id});

        url = `${url}?${query.toString()}`;
        
        const headers = new Headers({});
        const fetchBlob = core.services.data.fetchAll;

        setDownloadingFile(true);
        setDownloadingFormat(format);

        fetchBlob(url,{headers}).then(result => {
            setDownloadingFormat(null);
            const blob = result instanceof ArrayBuffer ? new Blob([result]) : result;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `intersecao_resultados.${format == "csv" ? "zip" : format}`
            document.body.appendChild(a);
            a.click();    
            a.remove();

            toastDialog.current.show({ severity: 'success', summary: t("information", "Informação"), detail: `${t("successFileGenerateMsg", "O ficheiro foi gerado com sucesso")}.` });
        })
        .catch(e => {
            toastDialog.current.show({ severity: 'warn', summary: t("error", "Error"), detail: `${t("errorFileGenerateMsg", "Ocorreu um erro ao gerar o ficheiro")}.` });
        })
        .finally(d => {
            setDownloadingFile(false);
            setDownloadingFormat(null);
        });
    }

    useEffect(() => {

        let new_data = [];

        if (groupResults) {
            new_data = groupResultsData(results);
        } else {
            new_data = results?.layers?.map( (l, index) => {
                return {
                    key: String(index),
                    ...l
                }
            });
        }
        setItems(new_data);

        setSelectedRows({});
        setSelectAll({});
        setShowGeometry(false);

        setDownloadingFile(false);
        setDownloadingFormat(null);
    }, [results, groupResults]);

    useEffect(() => {
        if (!items?.length) return;
        expandAll();
    }, [items]);

    useEffect(() => {
        if (!mainMap) return;
        if (!intersectLayer.current) return;

        intersectLayer.current.getSource().getFeatures().forEach((f) => {
            if (f.get('_type') === 'output') {
                intersectLayer.current.getSource().removeFeature(f);
            }
        });

        if (showGeometry && results?.output_geom) {
            const geomEWKT = results?.output_geom;
            if (!geomEWKT) return;
            const srid = geomEWKT.split(';')[0].split('=')[1];
            const wkt = geomEWKT.split(';')[1];
            if (geomEWKT) {
                const format = new OlWKT();
                const options = {
                    dataProjection: `EPSG:${srid}`,
                    featureProjection: mainMap.getView().getProjection().getCode()
                }
                const geom = options ? format.readGeometry(wkt, options) : format.readGeometry(wkt);

                const feature = new OlFeature({_componentId: componentId, _type: 'output', geometry: geom});
                intersectLayer.current.getSource().addFeature(feature);
            }
        }

        mainMap.getView().changed();

    }, [showGeometry]);

    useEffect(() => {
        if (!intersectLayer.current) return;

        intersectLayer.current.getSource().getFeatures().forEach((f) => {
            if (f.get('_componentId') == componentId && f.get('_type') == 'results') {
                intersectLayer.current.getSource().removeFeature(f);
            }
        });

        for (const [key, recs] of Object.entries(selectedRows)) {
            recs.forEach((rec) => {
                const geomEWKT = rec?.geom;
                if (!geomEWKT) return;
                const srid = geomEWKT.split(';')[0].split('=')[1];
                const wkt = geomEWKT.split(';')[1];
                if (geomEWKT) {
                    const format = new OlWKT();
                    const options = {
                        dataProjection: `EPSG:${srid}`,
                        featureProjection: mainMap.getView().getProjection().getCode()
                    }
                    const geom = options ? format.readGeometry(wkt, options) : format.readGeometry(wkt);

                    const feature = new OlFeature({...rec, _layerKey: key, _componentId: componentId, _type: 'results', geometry: geom});
                    intersectLayer.current.getSource().addFeature(feature);
                }
            });
        }

        mainMap.getView().changed();

    }, [selectedRows]);

    const expandAll = () => {
        const _expandedRows = {};
        items.forEach((p) => (_expandedRows[`${p.key}`] = true));
        setExpandedRows(_expandedRows);
    };

    const collapseAll = () => {
        setExpandedRows(null);
    };

    const showTable = () => {
        const key = `${componentId}-table`;

        let header = intersectConfig.header;
        header = i18n.translateValue(header, header);
        header = props?.title && header ? `${props?.title} - ${header}` : props?.title || header || "Interseção de Temas";

        if (viewer?.dialogWindows?.length > 0) {
            const dw = viewer.dialogWindows.find(w => w.key === key);
            if (dw) {           
                const config = { 
                    ...dw.config,
                    header: header,
                    className: 'sg-intersect-layers-results-table-dialog'
                }
                
                const childKey = new Date().getTime();
                const child = <ResultsTablesTabs 
                    groups={results?.groups || []}
                    layers={results?.layers || []}
                    showResultMeasure={showResultMeasure}
                    showResultPercentage={showResultPercentage}
                    />
    
                dispatch(viewer_update_dialog_window({config: config, child: child, visible: true }));
                return;
            }
        }
    
        const data = {};

        const config = Object.assign({}, data?.dialogOptions);
        config.key = key;
        config.header = header;
        config.maximizable = true;
        config.style={ width: '75vw', height: '75vh' };
        config.className ='sg-intersect-layers-results-table-dialog'
    
        const childKey = new Date().getTime();
        const child = <ResultsTablesTabs 
            groups={results?.groups || []}
            layers={results?.layers || []}
            showResultMeasure={showResultMeasure}
            showResultPercentage={showResultPercentage}
            />;
    
        dispatch(viewer_add_dialog_window({config, child, onClose: data?.onClose}));
    }

    const allowExpansion = (rowData) => {
        return rowData.results.length > 0;
    };

    const onSelectionChange = (event, data) => {
        const _selectedRows = {...selectedRows}
        _selectedRows[data.key] = event.value;
        setSelectedRows(_selectedRows);

        const _selectAll = {...selectAll}
        _selectAll[data.key] = _selectedRows[data.key].length === data.results.length;
        setSelectAll(_selectAll);
    };

    const onSelectAllChange = (event, data) => {
        const _selectAll = {...selectAll}
        _selectAll[data.key] = event.checked;
        setSelectAll(_selectAll);

        const _selectedRows = {...selectedRows}
        if (event.checked) {
            _selectedRows[data.key] = [...data.results];
        } else {
            _selectedRows[data.key] = [];
        }
        setSelectedRows(_selectedRows);
    };

    const recordBodyTemplate = (rowData, layerKey, cfg) => {
        const precision = cfg?.precision || 100;

        const fields = rowData?.fields || cfg?.fields || [];

        const showAreaTable = rowData?.dimension == 2 && 
            (intersectConfig?.showResultMeasure !== false || intersectConfig?.showResultPercentage !== false);

        const showLengthTable = rowData?.dimension == 1 && 
            (intersectConfig?.showResultMeasure !== false || intersectConfig?.showResultPercentage !== false);

        return (
            <>
                <table onClick={ (e) => selectFeature(rowData, layerKey) } className='sg-intersect-results-table sg-intersect-results-table-record'>
                    { fields?.length ? fields.map( (col, index) => {
                        const _title = col?.title ? t(col.title, col.title) : col.alias
                        return (
                            <tr key={index}>
                                <td><span className='font-bold'>{`${_title}: `}</span>{rowData[col.field]}</td>
                            </tr>
                        )
                    }) : null }
                </table>
                { showAreaTable &&
                <div className='flex'>
                    <table className='sg-intersect-results-table-area'>
                        <tr>
                            { showResultMeasure !== false ? <th>{`${t("area", "Área")} (m²)`}</th> : null }
                            { showResultPercentage !== false ? <th>%</th> : null }
                        </tr>
                        <tr>
                        { showResultMeasure !== false ? 
                            <td>{rowData.area != null ? (Math.round(rowData.area  * precision)  / precision) : rowData.area}</td> 
                            : null }
                        { showResultPercentage !== false ?
                            <td>{rowData.percent != null ? (Math.round(rowData.percent * precision)  / precision) : rowData.percent}</td>
                            : null }
                        </tr>
                    </table>
                </div>
                }
                { showLengthTable &&
                <div className='flex'>
                    <table className='sg-intersect-results-table-area'>
                        <tr>
                            { showResultMeasure !== false ? <th>{`${t("length", "Comprimento")} (m)`}</th> : null }
                            { showResultPercentage !== false ? <th>%</th> : null }
                        </tr>
                        <tr>
                        { showResultMeasure !== false ? 
                            <td>{rowData.length != null ? (Math.round(rowData.length  * precision)  / precision) : rowData.length}</td> 
                            : null }
                        { showResultPercentage !== false ?
                            <td>{rowData.percent != null ? (Math.round(rowData.percent * precision)  / precision) : rowData.percent}</td>
                            : null }
                        </tr>
                    </table>
                </div>
                }
            </>
        )
    };

    const itemBodyTemplate = (rowData) => {
        let title = rowData?.title || rowData?.group;
        if (results?.groups?.length) {
            const _group = results.groups.find((g) => g.name === rowData?.group);
            if (_group?.title) {
                title = t(_group.title || title, _group.title || title);
            }
        }
        return <label className='font-bold'>{title}</label>;
    };

    const rowExpansionTemplate = (data) => {
        let header = null;
        if (data?.results?.length > 1) {
            header = (
                <div className="flex justify-content-end mt-2 mr-2 font-normal">
                    <Checkbox name="selectAll" onChange={(e) => onSelectAllChange(e, data)} checked={selectAll[data.key]} />
                    <label htmlFor="selectAll" className="ml-1">{t("showAll", "Ver todos")}</label>
                </div>
            );
        }

        if (intersectConfig?.showResultGeometries === false) {
            return (
                <DataTable value={data.results} className='sg-intersect-results-table'>
                    <Column header="Ver todos" body={(rowData) => recordBodyTemplate(rowData, data.key, { fields: data?.fields})} headerStyle={{ display: 'none' }}></Column>
                </DataTable>
            );
        }

        return (
            <DataTable value={data.results} selection={selectedRows[data.key]}
                selectionMode="checkbox"
                onSelectionChange={(event) => onSelectionChange(event, data)}
                header={header}
                className='sg-intersect-results-table'>
                <Column selectionMode="multiple" headerStyle={{ width: '1rem', display: 'none' }} />
                <Column header="Ver todos" body={(rowData) => recordBodyTemplate(rowData, data.key, {...data})} headerStyle={{ display: 'none' }}></Column>
            </DataTable>
        );

    };

    const exportToolbar = exportFormats?.length > 3 ? (
        <SplitButton label={t("export", "Exportar")} icon="pi pi-upload" size="small" outlined 
            disabled={downloadingFile}
            loading={downloadingFile}            
            model={exportFormats.map((format) => {
                return {
                    label: fileFormats?.[format]?.label || format.toUpperCase(),
                    icon: `pi ${ fileFormats?.[format]?.icon ? fileFormats[format].icon : "p-file" }`,
                    command: () => {
                        exportResults(format);
                    }
                }
            })}
         />
    ) : (
        <React.Fragment>
            { 
                exportFormats.map((format, index) => {
                    return (
                        <Button key={index} icon={`pi ${ fileFormats?.[format]?.icon ? fileFormats[format].icon : "p-file" }`} className="mr-2"
                            label={format.toUpperCase()} link
                            disabled={downloadingFile}
                            loading={downloadingFile && downloadingFormat == format}
                            onClick={() =>  {
                                exportResults(format);
                            }
                        }/>
                    );
                })
            }
        </React.Fragment>
    );

    const header = (
        <div className="flex justify-content-between flex-wrap">
            { showTablesButton && <div className="flex flex-wrap justify-content-end gap-2 my-1">
                <Button icon="pi pi-table" label={t("tables", "Tabelas")} onClick={showTable} text size="small" />
            </div> }
            <div className="flex flex-wrap justify-content-end gap-2 my-1">
                <Button icon="pi pi-plus" label={t("expand", "Expandir")} onClick={expandAll} text size="small" />
                <Button icon="pi pi-minus" label={t("collapse", "Recolher")} onClick={collapseAll} text size="small" />
            </div>
        </div>
    )

    return (
        <div className="mt-4">
            {showOnPortal(<Toast ref={toastDialog} />)}
            <h3>{t("results", "Resultados")}</h3>

            { intersectConfig?.showExport !== false ? <Toolbar end={exportToolbar} /> : null }

            <div className="text-right mb-2">

            </div>

            <div className="flex align-items-center my-3">
                <Checkbox name="showGeometry" value={showGeometry} onChange={(e) => setShowGeometry(!e.value)} checked={showGeometry} />
                <label htmlFor="showGeometry" className="ml-2">{t("showIntersectionGeometry", "Ver Geometria da Confrontação")}</label>
            </div>

            <div className="mt-2">
                <DataTable value={items} expandedRows={expandedRows} onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={rowExpansionTemplate}
                        dataKey="key" header={header} className='sg-intersect-results-table' showHeaders={false} >
                    <Column expander={allowExpansion} style={{ width: '1rem' }} />
                    <Column body={itemBodyTemplate} />
                </DataTable>
            </div>

        </div>
    )
}